import { useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { Issue, IssueLevel } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import { formatQuantity, isLocalDateInFuture } from "../Containers/containerDisplay";
import { MARK_SHIPMENT_SHIPPED_MUTATION, VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT } from "./api";
import { REFETCH, Shipment } from "./shipmentDetailTypes";
import { formatTotalsByUnit, storedDateToDateOnly } from "./shipmentDisplay";

function plannedShippedDateDefault(plannedShipDate: string | null | undefined): string | null {
  const today = DateTime.now().startOf("day");
  const plannedDay = storedDateToDateOnly(plannedShipDate);
  if (plannedDay) {
    const planned = DateTime.fromISO(plannedDay).startOf("day");
    if (planned.isValid && planned <= today) {
      return planned.toISODate();
    }
  }
  return today.toISODate();
}

type ShipFormValues = { shippedDate: string | null };

/**
 * Mirrors the server rule's key and message: the input serializes the picked
 * date to UTC midnight, so the server can no longer tell which calendar day the
 * user was on and keeps a looser end-of-day tolerance. Deciding it here — as the
 * container fill dialogs do — is what makes "tomorrow" wrong for a user whose
 * today is already the server's tomorrow.
 */
const SHIPPED_DATE_IN_FUTURE_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "shippedDateInFuture",
  message: "The shipped date cannot be in the future.",
  modelName: "WasteShipment",
  dataName: null,
  jurisdictions: [],
};

function toShipInput(shipmentId: string, values: ShipFormValues) {
  return {
    shipmentId,
    shippedDate: values.shippedDate
      ? (toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.shippedDate)) ?? null)
      : null,
  };
}

/**
 * What shipping will actually do to the containers on this load — the one thing
 * the shipment page itself cannot say.
 */
function shipEffectLines(preview: Shipment["shipPreview"]): string[] {
  return [
    preview.archivedCount > 0
      ? `archive ${preview.archivedCount} one-way ${preview.archivedCount === 1 ? "container" : "containers"}`
      : null,
    preview.emptiedTankCount > 0
      ? `empty ${preview.emptiedTankCount} ${preview.emptiedTankCount === 1 ? "tank" : "tanks"} and return ${preview.emptiedTankCount === 1 ? "it" : "them"} to service`
      : null,
  ].filter((effect): effect is string => effect !== null);
}

/** Everything standing between this shipment and the dock. */
function ShipBlockers({ blockers }: { blockers: Issue[] }) {
  return (
    <Alert severity="error">
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
        {blockers.length} {blockers.length === 1 ? "problem" : "problems"} to fix before this can
        ship
      </Typography>
      <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.25}>
        {blockers.map((issue, i) => (
          <Typography key={`${issue.key}-${i}`} component="li" variant="caption">
            {issue.message}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}

/** The consequences of shipping, when nothing is blocking it. */
function ShipEffects({ preview }: { preview: Shipment["shipPreview"] }) {
  const effects = shipEffectLines(preview);
  if (effects.length === 0 && preview.keepsBalance.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Shipping this will
      </Typography>
      <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.25}>
        {effects.map((effect) => (
          <Typography key={effect} component="li" variant="body2" color="text.secondary">
            {effect}
          </Typography>
        ))}
        {preview.keepsBalance.map((balance) => (
          <Typography
            key={balance.containerLabel}
            component="li"
            variant="body2"
            color="text.secondary"
          >
            leave {balance.containerLabel} on site with{" "}
            {formatQuantity(balance.remaining, balance.unit)}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * The ship form, validated against the server rule plus the one rule the server
 * cannot apply: the input serializes the picked date to UTC midnight, so only
 * the client still knows which calendar day the user was on.
 *
 * Seeded from the shipment's own standing issues so a blocked shipment says so
 * the moment the dialog opens, without waiting on a round-trip.
 */
function useShipForm(shipment: Shipment, defaultValues: ShipFormValues) {
  const [validateQuery] = useLazyQuery(VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT, {
    fetchPolicy: "no-cache",
  });

  const validateInput = useCallback(
    async (values: ShipFormValues): Promise<Issue[]> => {
      const input = toShipInput(shipment.id, values);
      if (!input.shippedDate) {
        return [];
      }
      if (isLocalDateInFuture(values.shippedDate)) {
        return [SHIPPED_DATE_IN_FUTURE_ISSUE];
      }
      const { data } = await validateQuery({ variables: { input } });
      return data?.markShipmentShippedInputValidator ?? [];
    },
    [validateQuery, shipment.id]
  );

  return useValidatingForm<ShipFormValues>(defaultValues, shipment.issues, validateInput, []);
}

/**
 * The commit step, not a second editor. What is on the shipment and how much
 * of each container leaves are decisions made on the shipment page, so this
 * confirms the date and shows the one thing the page cannot: what shipping
 * will actually do to each container, and whether anything blocks it.
 */
export function MarkShipmentShippedDialog({
  shipment,
  onClose,
}: {
  shipment: Shipment;
  onClose: () => void;
}) {
  const alerts = useAlerts();

  const defaultValues = useMemo<ShipFormValues>(
    () => ({
      // The planned date is what the user already told us this shipment was
      // going out on, so it beats a guess. Falls back to today when the
      // shipment was never planned, or when the plan is still in the future
      // and the waste is leaving now.
      shippedDate: plannedShippedDateDefault(shipment.plannedShipDate),
    }),
    [shipment.plannedShipDate]
  );

  const form = useShipForm(shipment, defaultValues);
  const { control, getValues, watch, issues } = form;

  const [markShipped, { loading }] = useMutation(MARK_SHIPMENT_SHIPPED_MUTATION, {
    refetchQueries: REFETCH,
  });

  const shippedDate = watch("shippedDate");
  const blocked = hasCriticalIssues(issues) || !shippedDate || loading;
  const blockers = issues.filter((issue) => issue.level === IssueLevel.Critical);
  const preview = shipment.shipPreview;

  const handleShip = async () => {
    if (blocked) {
      return;
    }
    const input = toShipInput(shipment.id, getValues());
    if (!input.shippedDate) {
      return;
    }
    try {
      await markShipped({ variables: { input } });
      alerts.success(`${shipment.manifestRequestNumber} is marked shipped.`);
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to mark the shipment shipped.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Mark ${shipment.manifestRequestNumber} shipped`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Mark shipped",
        loading,
        disabled: blocked,
        onClick: handleShip,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This records {formatTotalsByUnit(shipment.totalQuantityByUnit)} across{" "}
            {shipment.lines.length} {shipment.lines.length === 1 ? "container" : "containers"} as
            having left the site.
          </Typography>

          <Box>
            <DateField
              control={control}
              name="shippedDate"
              label="Shipped date"
              datePickerProps={{
                disableFuture: true,
                slotProps: { textField: { size: "small", sx: { width: 220 } } },
              }}
            />
          </Box>

          {blockers.length > 0 ? (
            <ShipBlockers blockers={blockers} />
          ) : (
            <ShipEffects preview={preview} />
          )}

          {issues
            .filter((issue) => issue.level !== IssueLevel.Critical)
            .map((issue, i) => (
              <Alert key={`${issue.key}-${i}`} severity="warning">
                {issue.message}
              </Alert>
            ))}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
